import React from 'react';
import { clsx } from 'clsx';
import { isEmpty } from 'lodash-es';

import { Tabs, TabsList, TabsPanel, TabsTab } from '../../Tabs/Tabs';
import { Modal, ModalContent, ModalTitle } from '../Modal';

import styles from './ModalWithTabs.module.scss';

// Anatomy
// <ModalWithTabs>
//   <ModalWithTabs.Tabs>
//     <ModalWithTabs.TabList>
//       <ModalWithTabs.Tab />
//     </ModalWithTabs.TabList>
//     <ModalWithTabs.TabPanel />
//   </ModalWithTabs.Tabs>
// </ModalWithTabs>

/**
 * =====================================
 * ModalWithTabs
 *
 * Compose the Modal component with Tabs.
 * =====================================
 * */
export const ModalWithTabs = ({
  children,
  title,
  popupProps,
  shouldShowCloseButtonIcon = true,
  contentProps,
  ...props
}: ModalWithTabs.Props) => {
  const { className: titleClassName = '', ...modalTitleProps } =
    typeof title === 'string'
      ? { children: title }
      : (title ?? { className: '' });

  return (
    <Modal
      shouldShowCloseButtonIcon={shouldShowCloseButtonIcon}
      popupProps={{
        ...popupProps,
        className: clsx(styles.modalWithTabs)
      }}
      {...props}
      width="700px"
    >
      <Modal.Content {...contentProps} padding="none">
        {!isEmpty(modalTitleProps) && (
          <Modal.Title
            className={clsx(styles.modalTitle, titleClassName)}
            {...modalTitleProps}
          />
        )}
        {children}
      </Modal.Content>
    </Modal>
  );
};

export namespace ModalWithTabs {
  export type Props = Modal.Props & {
    /** To render the title of the modal; either pass a string or the ModalTitle props. */
    title: string | ModalTitle.Props;
    contentProps: ModalContent.Props;
  };

  // sub-component props
  export type TabsProps = ModalTabs.Props;
  export type TabListProps = ModalTabList.Props;
  export type TabProps = ModalTab.Props;
  export type TabPanelProps = ModalTabPanel.Props;
}

/**
 * =====================================
 * ModalTabs
 *
 * A simple wrapper around the Tabs
 * component with styles
 * =====================================
 * */
export const ModalTabs = ({ className, ...props }: ModalTabs.Props) => (
  <Tabs className={clsx(styles.tabs, className)} {...props} />
);
ModalTabs.displayName = 'ModalTabs';

export namespace ModalTabs {
  export type Props = Tabs.Props & {
    /** Apply custom styles. */
    className?: string;
  };
}

/**
 * =====================================
 * ModalTabList
 *
 * A simple wrapper around the Tabs
 * component with styles
 * =====================================
 * */
export const ModalTabList = ({ className, ...props }: ModalTabList.Props) => (
  <Tabs.List className={clsx(styles.tabList, className)} {...props} />
);
ModalTabList.displayName = 'ModalTabList';

export namespace ModalTabList {
  export type Props = TabsList.Props & {
    /** Apply custom styles. */
    className?: string;
  };
}

/**
 * =====================================
 * ModalTab
 *
 * The interactive tab button
 * =====================================
 * */
export const ModalTab = ({ className, ...props }: ModalTab.Props) => (
  <Tabs.Tab className={clsx(styles.tab, className)} {...props} />
);

ModalTab.displayName = 'ModalTab';

export namespace ModalTab {
  export type Props = TabsTab.Props & {
    /** Apply custom styles. */
    className?: string;
  };
}
/**
 * =====================================
 * ModalTabPanel
 *
 * The content to display when the tab
 * is selected.
 * =====================================
 * */
export const ModalTabPanel = ({ className, ...props }: ModalTabPanel.Props) => (
  <Tabs.Panel className={clsx(styles.tabPanel, className)} {...props} />
);
ModalTabPanel.displayName = 'ModalTabPanel';

export namespace ModalTabPanel {
  export type Props = TabsPanel.Props & {
    /** Apply custom styles. */
    className?: string;
  };
}

/**
 * =====================================
 * Compound Components
 * =====================================
 * */
ModalWithTabs.Tabs = ModalTabs;
ModalWithTabs.TabList = ModalTabList;
ModalWithTabs.Tab = ModalTab;
ModalWithTabs.TabPanel = ModalTabPanel;
